import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Code2, Wind, Box, Database, Waypoints, Cloud, Brain, Sparkles, Cpu, Languages, BarChart3, GitBranch, Terminal, Eye, Workflow, Lamp, Puzzle, Calendar, Users, Lightbulb, Layout, BrainCircuit, Wrench, UserCheck } from 'lucide-react';
import { FaGithub, FaReact, FaPython, FaPhp, FaLaravel, FaJava, FaJs, FaNodeJs, FaDocker, FaHtml5, FaCss3Alt } from "react-icons/fa";

const skillsData = {
  frontend: [{ name: 'React JS'}, { name: 'JavaScript'},{ name: 'HTML'},{ name: 'CSS'},{ name: 'Tailwind CSS'},{ name: 'Bootstrap' } ],
  backend: [ { name: 'Node.js'}, { name: 'Laravel'}, { name: 'MongoDB'}, { name: 'REST APIs'}, { name: 'MySQL'}, { name: 'PHP'}, { name: 'Python'}, { name: 'Java'} ],
  ai_ml: [ { name: 'Machine Learning'}, { name: 'Deep Learning'}, { name: 'TensorFlow'}, { name: 'NLP'}, { name: 'Big Data'}, { name: 'Data Science'}],
  devops: [ { name: 'Docker'}, { name: 'GitHub'}, { name: 'Git'}, { name: 'Slack'}],
  tools: [ { name: 'VS Code'}, { name: 'Figma'}, { name: 'Postman'}, { name: 'Eclipse'}, { name: 'MySQL Workbench'}, { name: 'StarUML'}, { name: 'Jira'}, { name: 'Vercel'}],
  soft: [ { name: 'Communication'}, { name: 'Problem Solving'}, { name: 'Time Management'}, { name: 'Adaptability'}, { name: 'Team Collaboration'}, { name: 'Leadership'}],
  learning: [ { name: 'Deep Learning'}, { name: 'Natural Language Processing'}, { name: 'Windows Server'}, { name: 'Big Data'}, { name: 'Data Science'}],
};

const skillIconMap = {
  
  'React JS': { Icon: FaReact, color: '#61DAFB' },
  'JavaScript': { Icon: FaJs, color: '#F7DF1E' },
  'CSS': { Icon: FaCss3Alt, color: '#1572B6' },
  'HTML': { Icon: FaHtml5, color: '#E34F26' },
  'Tailwind CSS': { Icon: Wind, color: '#06B6D4' },
  'Bootstrap': { Icon: Box, color: '#7952B3' },

  'Node.js': { Icon: FaNodeJs, color: '#339933' },
  'Laravel': { Icon: FaLaravel, color: '#FF6C37' },
  'MongoDB': { Icon: Database, color: '#47A248' },
  'REST APIs': { Icon: Waypoints, color: '#FF6C37' },
  'MySQL': { Icon: Database, color: '#4479A1' },
  'PHP': { Icon: FaPhp, color: '#777BB4' },
  'Python': { Icon: FaPython, color: '#3776AB' },
  'Java': { Icon: FaJava, color: '#007396' },

  'Machine Learning': { Icon: Brain, color: '#FF6F00' },
  'Deep Learning': { Icon: Sparkles, color: '#8A2BE2' },
  'TensorFlow': { Icon: Cpu, color: '#FF6F00' },
  'NLP': { Icon: Languages, color: '#1E90FF' },
  'Big Data': { Icon: Database, color: '#DC143C' },
  'Data Science': { Icon: BarChart3, color: '#4B0082' },

  'Docker': { Icon: FaDocker, color: '#2496ED' },
  'GitHub': { Icon: FaGithub, color: '#181717' },
  'Git': { Icon: GitBranch, color: '#F05032' },
  'Slack': { Icon: Cloud, color: '#4A154B' },

  'VS Code': { Icon: Terminal, color: '#007ACC' },
  'Figma': { Icon: Eye, color: '#F24E1E' },
  'Postman': { Icon: Workflow, color: '#FF6C37' },
  'Eclipse': { Icon: Lamp, color: '#2C2255' },
  'MySQL Workbench': { Icon: Database, color: '#4479A1' },
  'StarUML': { Icon: Puzzle, color: '#FFA500' },
  'Jira': { Icon: Calendar, color: '#0052CC' },
  'Vercel': { Icon: Cloud, color: '#000000' },

  'Communication': { Icon: Users, color: '#6C757D' },
  'Problem Solving': { Icon: Lightbulb, color: '#FFC107' },
  'Time Management': { Icon: Calendar, color: '#28A745' },
  'Adaptability': { Icon: Waypoints, color: '#17A2B8' },
  'Team Collaboration': { Icon: Users, color: '#6F42C1' },
  'Leadership': { Icon: Lightbulb, color: '#DC3545' },
};

const defaultIcon = { Icon: Code2, color: '#6B7280' };

const Skills = () => {
  const [activeCategory, setActiveCategory] = useState('frontend');

  const categories = [
    { key: 'frontend', label: 'Frontend', icon: Layout },
    { key: 'backend', label: 'Backend', icon: Database },
    { key: 'ai_ml', label: 'AI/ML', icon: BrainCircuit },
    { key: 'devops', label: 'DevOps', icon: Terminal },
    { key: 'tools', label: 'Tools', icon: Wrench },
    { key: 'soft', label: 'Soft Skills', icon: UserCheck },
  ];

  const fadeIn = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
  };

  return (
    <section id="skills" className="py-20 bg-white dark:bg-black">
      <div className="container mx-auto px-4 max-w-7xl">

        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
        >
          <h2 className="text-3xl text-gray-900 dark:text-white mb-3 flex items-center justify-center gap-2">
            <Code2 className="w-7 h-7 text-indigo-500 dark:text-indigo-400" />
            Skills & Expertise
          </h2>
          <div className="w-16 h-px bg-gray-300 dark:bg-gray-700 mx-auto" />
          <p className="text-gray-600 dark:text-gray-400 max-w-5xl mx-auto text-base font-light leading-relaxed mt-2">
            A specialized collection of technologies and frameworks I use to engineer scalable systems, intelligent models, and high-impact digital experiences.
          </p>
        </motion.div>

        {/* Category tabs */}
        <div className="mb-10 w-full overflow-hidden">
          <div className="flex items-center justify-start sm:justify-center gap-6 overflow-x-auto no-scrollbar pb-2 px-4 scroll-smooth">
            {categories.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                className={`group pb-2 text-sm font-medium transition-all flex items-center gap-2 border-b-2 whitespace-nowrap shrink-0 ${
                  activeCategory === key
                    ? 'text-gray-900 dark:text-white border-indigo-500'
                    : 'text-gray-400 dark:text-gray-500 border-transparent hover:text-gray-600 dark:hover:text-gray-300'
                }`}
                onClick={() => setActiveCategory(key)}
              >
                <Icon
                  className={`w-4 h-4 transition-transform duration-300 group-hover:-translate-y-0.5 ${
                    activeCategory === key ? 'text-indigo-500' : 'text-gray-400'
                  }`}
                />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Skills with icons */}
        <motion.div
          key={activeCategory}
          className="flex flex-wrap justify-center gap-x-4 gap-y-3"
          initial="hidden"
          animate="visible"
          variants={{
            hidden: { opacity: 0 },
            visible: { opacity: 1, transition: { staggerChildren: 0.03 } }
          }}
        >
          {skillsData[activeCategory]?.map((skill, index) => {
            const { Icon, color } = skillIconMap[skill.name] || defaultIcon;
            return (
              <motion.span
                key={index}
                variants={fadeIn}
                className="inline-flex items-center gap-1.5 text-gray-700 dark:text-gray-300 text-base"
                whileHover={{ y: -2, scale: 1.02 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <Icon size={18} color={color} className="shrink-0" />
                <span>{skill.name}</span>
                {index < skillsData[activeCategory].length - 1 && (
                  <span className="text-gray-300 dark:text-gray-600 mx-1.5">•</span>
                )}
              </motion.span>
            );
          })}
        </motion.div>

        {/* Currently learning */}
        <motion.div
          className="mt-12 text-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
        >
          <p className="text-sm text-gray-500 dark:text-gray-400">
            <span className="font-medium text-gray-700 dark:text-gray-300">Currently exploring:</span>{' '}
            {skillsData.learning?.map(l => l.name).join(' • ')}
          </p>
        </motion.div>

      </div>
    </section>
  );
};

export default Skills;